import isString from 'lodash/isString';
import has from 'lodash/has';
import extend from 'lodash/extend';
import { localForage } from '@Utils/Storage';
import { version } from '@Constants/BuildInfo';
import uuid from 'uuid/v4';
import { DbStorageErrorsModal } from '@Services/AppDBStorage/constants';
import NotifyDialogsService from '@Containers/Dialogs/NotifyDialogsService';
import { AppStorage } from '@Services/AppStorage/AppStorage';
import * as BrowserHelper from '@Services/BrowserHelper';
/* eslint-disable class-methods-use-this */
/* eslint-disable  consistent-return */
// eslint-disable-next-line import/prefer-default-export
export class AppDBStorage {
  static globalDBStorage = new AppDBStorage();

  constructor(ns) {
    this._ns = ns;
    this._isErrorShown = false;
    this._isMobile = BrowserHelper.detectMobileDevice();
    this._appStorage = AppStorage.instance;
  }

  getDefaultValue() {
    return null;
  }

  async set(key, value) {
    return this._set(key, value);
  }

  async get(key) {
    return this._get(key);
  }

  async setNew(value) {
    const id = this.uniqId();
    try {
      await this._set(id, value);
      return id;
    } catch (e) {
      Logger.reject(e);
    }
  }

  async update(key, fn) {
    return this._change(key, fn);
  }

  async remove(key) {
    // Always try to remove from localStorage (mobile primary, desktop backup)
    this._removeFromLocalStorage(key);

    const fullKey = `${this._ns}${key}`;
    try {
      return await localForage.removeItem(fullKey);
    } catch (e) {
      Logger.warn('AppDBStorage: Failed to remove from IndexedDB:', e);
    }
  }

  _removeFromLocalStorage(key) {
    if (this._appStorage && this._appStorage.remove) {
      this._appStorage.remove(`${this._ns}${key}`);
    }
  }

  async clear() {
    return this._each(
      (value, key) => {
        this.remove(key);
      },
      { ignoreItemParser: true }
    );
  }

  async cleanUp() {
    return this._each(
      (value, key) => {
        if (this._unserialize(value).v !== version) {
          this.remove(key);
        }
      },
      { ignoreItemParser: true }
    );
  }

  async getAll() {
    try {
      const result = [];
      await this._each((value) => {
        result.push(value);
      });
      return result;
    } catch (e) {
      Logger.reject(e);
    }
  }

  uniqId() {
    return uuid();
  }

  async _set(key, value) {
    // For mobile devices, use localStorage as primary storage
    if (this._isMobile) {
      // Use AppStorage with proper serialization for mobile
      if (this._appStorage?.set) {
        try {
          const wrappedData = { c: value, v: version };
          this._appStorage.set(`${this._ns}${key}`, wrappedData);
        } catch (e) {
          Logger.warn('AppDBStorage: Mobile localStorage setItem failed:', e);
        }
      }
      return;
    }
    const fullKey = `${this._ns}${key}`;
    try {
      const result = await localForage.setItem(fullKey, this._serialize({ c: value, v: version }));

      // On desktop, backup to localStorage in case of future mobile access
      this._setToLocalStorage(key, value);

      return result;
    } catch (e) {
      // Fallback to localStorage if IndexedDB fails
      this._setToLocalStorage(key, value);

      if (has(DbStorageErrorsModal, e.name)) {
        if (!this._isErrorShown) {
          const modalOpts = extend({}, DbStorageErrorsModal[e.name], {
            onConfirm: () => {
              this._isErrorShown = false;
            },
            trackingOptions: {
              message: `${DbStorageErrorsModal.title} ${DbStorageErrorsModal.text}`,
              component: 'Database storage',
            },
          });

          NotifyDialogsService.openModalWindow(modalOpts);
          this._isErrorShown = true;
        }
      } else {
        Logger.error('Unknown error in localforage.setItem:', e);
      }
    }
  }

  _serialize(item) {
    return JSON.stringify(item);
  }

  _unserialize(serializedItem) {
    if (serializedItem && isString(serializedItem)) {
      return JSON.parse(serializedItem);
    }
    return serializedItem;
  }

  async _get(key) {
    // For mobile devices, try localStorage first with proper deserialization
    if (this._isMobile) {
      const localStorageValue = this._getFromLocalStorageWithDeserialization(key);
      if (localStorageValue !== null) {
        return localStorageValue;
      }
    }

    const fullKey = `${this._ns}${key}`;
    try {
      const serializedValue = await localForage.getItem(fullKey);
      const value = this._unserialize(serializedValue);
      if (value && value.v === version) {
        const result = value.c == null ? this.getDefaultValue() : value.c;

        // On mobile, backup successful IndexedDB retrieval to localStorage
        if (this._isMobile && result !== null) {
          this._setToLocalStorage(key, result);
        }

        return result;
      }
      return this.getDefaultValue();
    } catch (e) {
      Logger.warn('AppDBStorage: Failed to retrieve from IndexedDB:', e);
      // On mobile, fallback to localStorage if IndexedDB fails
      if (this._isMobile) {
        const localStorageValue = this._getFromLocalStorageWithDeserialization(key);
        if (localStorageValue !== null) {
          return localStorageValue;
        }
      }
      return this.getDefaultValue();
    }
  }

  _getFromLocalStorage(key) {
    try {
      if (this._appStorage?.get) {
        return this._appStorage.get(`${this._ns}${key}`);
      }
    } catch (e) {
      Logger.warn('AppDBStorage: Failed to retrieve from localStorage:', e);
    }
    return null;
  }

  _getFromLocalStorageWithDeserialization(key) {
    try {
      if (this._appStorage?.get) {
        const data = this._appStorage.get(`${this._ns}${key}`);
        if (data && data.v === version) {
          return data.c == null ? this.getDefaultValue() : data.c;
        }
      }
      // Fallback to old format for backward compatibility
      return this._getFromLocalStorage(key);
    } catch (e) {
      Logger.warn('AppDBStorage: Failed to retrieve from localStorage with deserialization:', e);
      // Fallback to old format
      return this._getFromLocalStorage(key);
    }
  }

  _setToLocalStorage(key, value) {
    if (this._appStorage?.set) {
      this._appStorage.set(`${this._ns}${key}`, value);
    }
  }

  async _change(key, fn) {
    try {
      const item = await this._get(key);
      const result = fn(item);
      await this._set(key, result);
      return result;
    } catch (e) {
      Logger.reject(e);
    }
  }

  async _each(callback, options) {
    const ignoreParser = !!options?.ignoreItemParser;
    return localForage.iterate((value, key) => {
      if (key.indexOf(this._ns) === 0) {
        // eslint-disable-next-line no-param-reassign
        value = ignoreParser ? value : this._unserialize(value)?.c;
        return callback(value, key.replace(this._ns, ''));
      }
    });
  }
}
